// Enhanced Workflow Designer with Chat Interface and Auto-Generation

import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
  Connection,
  Edge,
  Node,
  ReactFlowProvider,
  useReactFlow,
  Panel
} from 'reactflow';
import {
  Box,
  Paper,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Avatar,
  Chip,
  Tab,
  Tabs,
  Card,
  CardContent
} from '@mui/material';
import {
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  AutoAwesome as AIIcon,
  AccountTree as FlowIcon,
  Chat as ChatIcon,
  Psychology as BrainIcon,
  PlayArrow as RunIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';

import { WorkflowDefinition, WorkflowNode, WorkflowEdge } from '../types/workflow.ts';
import { ConnectorManager } from '../engine/ConnectorManager.ts';
import { WorkflowEngine } from '../engine/WorkflowEngine.ts';

// Custom Node Components (imported from existing)
import ActionNode from './nodes/ActionNode.tsx';
import ConditionNode from './nodes/ConditionNode.tsx';
import TriggerNode from './nodes/TriggerNode.tsx';
import LoopNode from './nodes/LoopNode.tsx';
import TransformNode from './nodes/TransformNode.tsx';
import WaitNode from './nodes/WaitNode.tsx';
import ApprovalNode from './nodes/ApprovalNode.tsx';
import NotificationNode from './nodes/NotificationNode.tsx';
import AssignmentNode from './nodes/AssignmentNode.tsx';
import ScreenNode from './nodes/ScreenNode.tsx';
import VariableNode from './nodes/VariableNode.tsx';
import SubflowNode from './nodes/SubflowNode.tsx';

import 'reactflow/dist/style.css';

const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  condition: ConditionNode,
  loop: LoopNode,
  transform: TransformNode,
  wait: WaitNode,
  approval: ApprovalNode,
  notification: NotificationNode,
  assignment: AssignmentNode,
  screen: ScreenNode,
  variable: VariableNode,
  subflow: SubflowNode
};

interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  flowSuggestion?: WorkflowDefinition;
}

interface ChatWorkflowDesignerProps {
  workflow?: WorkflowDefinition;
  connectorManager: ConnectorManager;
  workflowEngine: WorkflowEngine;
  onSave?: (workflow: WorkflowDefinition) => void;
  onRun?: (workflow: WorkflowDefinition) => void;
  readOnly?: boolean;
}

const ChatWorkflowDesigner: React.FC<ChatWorkflowDesignerProps> = ({
  workflow,
  connectorManager,
  workflowEngine,
  onSave,
  onRun,
  readOnly = false
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(workflow?.nodes || []);
  const [edges, setEdges, onEdgesChange] = useEdgesState(workflow?.edges || []);
  const [activeTab, setActiveTab] = useState(0); // 0: Chat, 1: Elements
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      text: "Hi! I'm your AI Flow Builder assistant. Describe what you want to automate and I'll help you create the perfect workflow. For example: 'Create a flow to approve customer orders over $1000'",
      sender: 'ai',
      timestamp: new Date()
    }
  ]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [workflowName, setWorkflowName] = useState(workflow?.name || 'AI-Generated Workflow');
  const [drawerOpen, setDrawerOpen] = useState(true);

  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const { project } = useReactFlow();

  // Auto-scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Logical ordering of node categories
  const nodeCategories = [
    {
      title: 'Flow Triggers',
      icon: '🚀',
      color: '#FF6B35',
      order: 1,
      nodes: [
        { type: 'trigger', label: 'Record-Triggered Flow', icon: '📝', description: 'Trigger when records are created, updated, or deleted', color: '#FF6B35' },
        { type: 'trigger', label: 'Schedule-Triggered Flow', icon: '⏰', description: 'Run flow on a schedule', color: '#FF6B35' },
        { type: 'trigger', label: 'Platform Event-Triggered', icon: '📡', description: 'Trigger from platform events', color: '#FF6B35' },
        { type: 'trigger', label: 'Screen Flow', icon: '🖥️', description: 'Interactive screen-based flow', color: '#FF6B35' },
        { type: 'trigger', label: 'Autolaunched Flow', icon: '🎯', description: 'Flow launched programmatically', color: '#FF6B35' }
      ]
    },
    {
      title: 'Logic Elements',
      icon: '🧠',
      color: '#45B7D1',
      order: 2,
      nodes: [
        { type: 'condition', label: 'Decision', icon: '🔀', description: 'Branch flow based on conditions', color: '#45B7D1' },
        { type: 'assignment', label: 'Assignment', icon: '=', description: 'Set variable values', color: '#45B7D1' },
        { type: 'loop', label: 'Loop', icon: '🔄', description: 'Iterate through collections', color: '#45B7D1' },
        { type: 'transform', label: 'Transform', icon: '🔄', description: 'Transform data format', color: '#45B7D1' }
      ]
    },
    {
      title: 'Data Elements',
      icon: '🗄️',
      color: '#4ECDC4',
      order: 3,
      nodes: [
        { type: 'action', label: 'Get Records', icon: '🔍', description: 'Retrieve records from database', color: '#4ECDC4' },
        { type: 'action', label: 'Create Records', icon: '➕', description: 'Create new records', color: '#4ECDC4' },
        { type: 'action', label: 'Update Records', icon: '✏️', description: 'Update existing records', color: '#4ECDC4' },
        { type: 'action', label: 'Delete Records', icon: '🗑️', description: 'Delete records', color: '#4ECDC4' },
        { type: 'action', label: 'Get Related Records', icon: '🔗', description: 'Get related child/parent records', color: '#4ECDC4' }
      ]
    },
    {
      title: 'Interaction Elements',
      icon: '👥',
      color: '#96CEB4',
      order: 4,
      nodes: [
        { type: 'screen', label: 'Screen', icon: '📱', description: 'Display interactive screens', color: '#96CEB4' },
        { type: 'action', label: 'Send Email', icon: '📧', description: 'Send email messages', color: '#96CEB4' },
        { type: 'action', label: 'Post to Chatter', icon: '💬', description: 'Post to Chatter feed', color: '#96CEB4' },
        { type: 'approval', label: 'Submit for Approval', icon: '✅', description: 'Submit records for approval', color: '#96CEB4' }
      ]
    },
    {
      title: 'Flow Control',
      icon: '🎛️',
      color: '#FFEAA7',
      order: 5,
      nodes: [
        { type: 'wait', label: 'Wait', icon: '⏸️', description: 'Pause flow execution', color: '#FFEAA7' },
        { type: 'subflow', label: 'Subflow', icon: '🔗', description: 'Call another flow', color: '#FFEAA7' },
        { type: 'action', label: 'Custom Apex', icon: '⚙️', description: 'Execute custom Apex code', color: '#FFEAA7' },
        { type: 'action', label: 'Call REST API', icon: '🌐', description: 'Make external API calls', color: '#FFEAA7' }
      ]
    }
  ].sort((a, b) => a.order - b.order); // Sort by logical order

  const onConnect = useCallback(
    (params: Connection) => {
      const edge: Edge = {
        ...params,
        id: `edge_${Date.now()}`,
        animated: true,
        style: { stroke: '#4CAF50', strokeWidth: 2 }
      };
      setEdges((eds) => addEdge(edge, eds));
    },
    [setEdges]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');
      const nodeData = JSON.parse(event.dataTransfer.getData('application/node-data'));

      if (typeof type === 'undefined' || !type || !reactFlowBounds) {
        return;
      }

      const position = project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNode: Node = {
        id: `node_${Date.now()}`,
        type,
        position,
        data: {
          label: nodeData.label,
          ...nodeData
        },
        ...nodeData
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [project, setNodes]
  );

  // AI Chat Handler
  const handleSendMessage = async () => {
    if (!currentMessage.trim() || isGenerating) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: currentMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setIsGenerating(true);

    // Simulate AI processing
    setTimeout(() => {
      const aiResponse = generateAIResponse(currentMessage);
      setChatMessages(prev => [...prev, aiResponse]);
      
      // If AI suggests a workflow, apply it
      if (aiResponse.flowSuggestion) {
        applyAIGeneratedFlow(aiResponse.flowSuggestion);
      }
      
      setIsGenerating(false);
    }, 2000);
  };

  // AI Response Generator
  const generateAIResponse = (userInput: string): ChatMessage => {
    const input = userInput.toLowerCase();
    
    // Order approval workflow
    if (input.includes('order') && input.includes('approval')) {
      return {
        id: Date.now().toString(),
        text: "I'll create an order approval workflow for you! This flow will trigger when new orders are created, check if the amount is over $1000, and route to a manager for approval.",
        sender: 'ai',
        timestamp: new Date(),
        flowSuggestion: createOrderApprovalFlow()
      };
    }
    
    // User registration workflow
    if (input.includes('user') && (input.includes('registration') || input.includes('signup'))) {
      return {
        id: Date.now().toString(),
        text: "Perfect! I'll create a user registration workflow that triggers when new users sign up, sends a welcome email, and creates their profile.",
        sender: 'ai',
        timestamp: new Date(),
        flowSuggestion: createUserRegistrationFlow()
      };
    }
    
    // Email notification workflow
    if (input.includes('email') && input.includes('notification')) {
      return {
        id: Date.now().toString(),
        text: "I'll set up an email notification workflow that can be triggered by various events and send customized emails to users.",
        sender: 'ai',
        timestamp: new Date(),
        flowSuggestion: createEmailNotificationFlow()
      };
    }
    
    // Default response
    return {
      id: Date.now().toString(),
      text: "I understand you want to create a workflow. Could you provide more details? For example:\n• 'Create approval flow for orders over $1000'\n• 'Send welcome email when users register'\n• 'Notify managers when tickets are created'\n• 'Update records based on status changes'",
      sender: 'ai',
      timestamp: new Date()
    };
  };

  // Pre-built workflow templates
  const createOrderApprovalFlow = (): WorkflowDefinition => {
    return {
      id: `workflow_${Date.now()}`,
      name: 'Order Approval Workflow',
      description: 'Automatically route high-value orders for manager approval',
      version: '1.0.0',
      nodes: [
        {
          id: 'trigger_1',
          type: 'trigger',
          data: { 
            label: 'New Order Created',
            icon: '📝',
            color: '#FF6B35'
          },
          position: { x: 100, y: 50 },
          configuration: { triggerType: 'record_create', object: 'Order' }
        },
        {
          id: 'condition_1',
          type: 'condition',
          data: { 
            label: 'Amount > $1000?',
            icon: '🔀',
            color: '#45B7D1'
          },
          position: { x: 100, y: 180 },
          configuration: { field: 'Amount', operator: 'greater_than', value: 1000 }
        },
        {
          id: 'approval_1',
          type: 'approval',
          data: { 
            label: 'Manager Approval',
            icon: '✅',
            color: '#96CEB4'
          },
          position: { x: 50, y: 320 },
          configuration: { approver: 'Manager', object: 'Order' }
        },
        {
          id: 'action_1',
          type: 'action',
          data: { 
            label: 'Auto-Approve Order',
            icon: '✅',
            color: '#4ECDC4'
          },
          position: { x: 200, y: 320 },
          configuration: { action: 'update_record', field: 'Status', value: 'Approved' }
        }
      ],
      edges: [
        { id: 'edge_1', source: 'trigger_1', target: 'condition_1' },
        { id: 'edge_2', source: 'condition_1', target: 'approval_1', sourceHandle: 'true' },
        { id: 'edge_3', source: 'condition_1', target: 'action_1', sourceHandle: 'false' }
      ],
      triggers: [],
      variables: [],
      settings: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'ai_assistant',
      status: 'draft'
    };
  };

  const createUserRegistrationFlow = (): WorkflowDefinition => {
    return {
      id: `workflow_${Date.now()}`,
      name: 'User Registration Workflow',
      description: 'Welcome new users and set up their profiles',
      version: '1.0.0',
      nodes: [
        {
          id: 'trigger_1',
          type: 'trigger',
          data: { 
            label: 'User Registered',
            icon: '👤',
            color: '#FF6B35'
          },
          position: { x: 100, y: 50 },
          configuration: { triggerType: 'record_create', object: 'User' }
        },
        {
          id: 'action_1',
          type: 'action',
          data: { 
            label: 'Send Welcome Email',
            icon: '📧',
            color: '#96CEB4'
          },
          position: { x: 100, y: 180 },
          configuration: { action: 'send_email', template: 'welcome_email' }
        },
        {
          id: 'action_2',
          type: 'action',
          data: { 
            label: 'Create User Profile',
            icon: '📝',
            color: '#4ECDC4'
          },
          position: { x: 100, y: 310 },
          configuration: { action: 'create_record', object: 'UserProfile' }
        }
      ],
      edges: [
        { id: 'edge_1', source: 'trigger_1', target: 'action_1' },
        { id: 'edge_2', source: 'action_1', target: 'action_2' }
      ],
      triggers: [],
      variables: [],
      settings: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'ai_assistant',
      status: 'draft'
    };
  };

  const createEmailNotificationFlow = (): WorkflowDefinition => {
    return {
      id: `workflow_${Date.now()}`,
      name: 'Email Notification Workflow',
      description: 'Send customized email notifications',
      version: '1.0.0',
      nodes: [
        {
          id: 'trigger_1',
          type: 'trigger',
          data: { 
            label: 'Event Triggered',
            icon: '📡',
            color: '#FF6B35'
          },
          position: { x: 100, y: 50 },
          configuration: { triggerType: 'platform_event' }
        },
        {
          id: 'action_1',
          type: 'action',
          data: { 
            label: 'Get Email Template',
            icon: '📄',
            color: '#4ECDC4'
          },
          position: { x: 100, y: 180 },
          configuration: { action: 'get_record', object: 'EmailTemplate' }
        },
        {
          id: 'action_2',
          type: 'action',
          data: { 
            label: 'Send Email',
            icon: '📧',
            color: '#96CEB4'
          },
          position: { x: 100, y: 310 },
          configuration: { action: 'send_email' }
        }
      ],
      edges: [
        { id: 'edge_1', source: 'trigger_1', target: 'action_1' },
        { id: 'edge_2', source: 'action_1', target: 'action_2' }
      ],
      triggers: [],
      variables: [],
      settings: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'ai_assistant',
      status: 'draft'
    };
  };

  // Apply AI-generated flow to canvas
  const applyAIGeneratedFlow = (flowDef: WorkflowDefinition) => {
    setNodes(flowDef.nodes as Node[]);
    setEdges(flowDef.edges as Edge[]);
    setWorkflowName(flowDef.name);
  };

  const onDragStart = (event: React.DragEvent, nodeType: string, nodeData: any) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.setData('application/node-data', JSON.stringify(nodeData));
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleSave = useCallback(() => {
    const workflowDefinition: WorkflowDefinition = {
      id: workflow?.id || `workflow_${Date.now()}`,
      name: workflowName,
      description: `Generated via AI chat interface`,
      version: workflow?.version || '1.0.0',
      nodes: nodes as WorkflowNode[],
      edges: edges as WorkflowEdge[],
      triggers: workflow?.triggers || [],
      variables: workflow?.variables || [],
      settings: workflow?.settings || {},
      createdAt: workflow?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: workflow?.createdBy || 'current_user',
      status: 'draft'
    };

    onSave?.(workflowDefinition);
  }, [nodes, edges, workflowName, workflow, onSave]);

  const handleRun = useCallback(() => {
    const workflowDefinition: WorkflowDefinition = {
      id: workflow?.id || `workflow_${Date.now()}`,
      name: workflowName,
      description: `Generated via AI chat interface`,
      version: workflow?.version || '1.0.0',
      nodes: nodes as WorkflowNode[],
      edges: edges as WorkflowEdge[],
      triggers: workflow?.triggers || [],
      variables: workflow?.variables || [],
      settings: workflow?.settings || {},
      createdAt: workflow?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: workflow?.createdBy || 'current_user',
      status: 'active'
    };

    onRun?.(workflowDefinition);
  }, [nodes, edges, workflowName, workflow, onRun]);

  return (
    <Box sx={{ height: '100%', display: 'flex' }}>
      {/* Enhanced Sidebar with Chat and Elements */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={drawerOpen}
        sx={{
          width: 400,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 400,
            boxSizing: 'border-box',
            position: 'relative',
            height: '100%',
            backgroundColor: '#FFFFFF',
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
            display: 'flex',
            flexDirection: 'column'
          },
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: '1px solid #E3E6E8' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#485869', mb: 2 }}>
            AI Flow Builder
          </Typography>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 48
              }
            }}
          >
            <Tab icon={<ChatIcon />} label="Chat" />
            <Tab icon={<FlowIcon />} label="Elements" />
          </Tabs>
        </Box>

        {/* Chat Interface */}
        {activeTab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Chat Messages */}
            <Box sx={{ 
              flex: 1, 
              overflowY: 'auto', 
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}>
              {chatMessages.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                    alignItems: 'flex-start',
                    gap: 1
                  }}
                >
                  {message.sender === 'ai' && (
                    <Avatar sx={{ bgcolor: '#6366f1', width: 32, height: 32 }}>
                      <AIIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                  )}
                  <Card
                    sx={{
                      maxWidth: '80%',
                      bgcolor: message.sender === 'user' ? '#6366f1' : '#f5f5f5',
                      color: message.sender === 'user' ? 'white' : 'inherit'
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                        {message.text}
                      </Typography>
                      {message.flowSuggestion && (
                        <Chip
                          icon={<BrainIcon />}
                          label="Flow Generated!"
                          color="success"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </CardContent>
                  </Card>
                  {message.sender === 'user' && (
                    <Avatar sx={{ bgcolor: '#485869', width: 32, height: 32 }}>
                      U
                    </Avatar>
                  )}
                </Box>
              ))}
              {isGenerating && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 5 }}>
                  <Avatar sx={{ bgcolor: '#6366f1', width: 32, height: 32 }}>
                    <AIIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Card sx={{ bgcolor: '#f5f5f5' }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="body2">
                        🤔 Thinking... Generating your workflow...
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              )}
              <div ref={chatEndRef} />
            </Box>

            {/* Chat Input */}
            <Box sx={{ p: 3, borderTop: '1px solid #E3E6E8' }}>
              <TextField
                fullWidth
                placeholder="Describe your workflow... (e.g., 'Create approval flow for orders over $1000')"
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                multiline
                maxRows={3}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f8f9fa'
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton 
                        onClick={handleSendMessage}
                        disabled={!currentMessage.trim() || isGenerating}
                        color="primary"
                        sx={{
                          bgcolor: '#6366f1',
                          color: 'white',
                          '&:hover': { bgcolor: '#5855eb' },
                          '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' }
                        }}
                      >
                        <SendIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
        )}

        {/* Elements Panel */}
        {activeTab === 1 && (
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            p: 3
          }}>
            {nodeCategories.map((category, categoryIndex) => (
              <Accordion 
                key={category.title} 
                defaultExpanded={categoryIndex < 2}
                sx={{
                  mb: 3,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E3E6E8',
                  borderRadius: '8px !important',
                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
                  '&:before': {
                    display: 'none',
                  },
                  '& .MuiAccordionSummary-root': {
                    minHeight: 64,
                    padding: '0 20px',
                    '&.Mui-expanded': {
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    }
                  },
                  '& .MuiAccordionDetails-root': {
                    padding: '16px 20px 20px 20px',
                    borderTop: '1px solid #E3E6E8',
                    backgroundColor: '#f8f9fa'
                  }
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon sx={{ color: '#485869' }} />}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '8px',
                        backgroundColor: category.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.3rem',
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {category.icon}
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#485869', fontSize: '1rem' }}>
                        {category.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6B757F', fontSize: '0.8rem' }}>
                        {category.nodes.length} elements
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {category.nodes.map((node, index) => (
                      <Box
                        key={index}
                        draggable
                        onDragStart={(e) => onDragStart(e, node.type, node)}
                        sx={{
                          cursor: 'grab',
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E3E6E8',
                          borderRadius: '8px',
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          transition: 'all 0.2s ease',
                          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                          '&:hover': {
                            backgroundColor: '#F5F6F7',
                            borderColor: '#C8CDD2',
                            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.12)',
                            transform: 'translateY(-1px)'
                          },
                          '&:active': {
                            cursor: 'grabbing',
                            transform: 'translateY(0px)'
                          }
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '6px',
                            backgroundColor: node.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.1rem',
                            flexShrink: 0,
                            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
                          }}
                        >
                          {node.icon}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#485869', mb: 0.5, fontSize: '0.9rem' }}>
                            {node.label}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6B757F', lineHeight: 1.3, fontSize: '0.75rem' }}>
                            {node.description}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Drawer>

      {/* Main Canvas */}
      <Box sx={{ flexGrow: 1, position: 'relative', backgroundColor: '#F5F6F7' }}>
        <div ref={reactFlowWrapper} style={{ width: '100%', height: '100%' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onDragOver={onDragOver}
            nodeTypes={nodeTypes}
            fitView
            attributionPosition="bottom-left"
          >
            <Background variant="dots" gap={20} size={1} color="#E3E6E8" />
            <Controls />
            <MiniMap />
            
            {/* Top Panel with Actions */}
            <Panel position="top-right">
              <Paper sx={{ p: 1, display: 'flex', gap: 0.5 }}>
                <IconButton 
                  size="small" 
                  onClick={() => setDrawerOpen(!drawerOpen)}
                  title={drawerOpen ? "Hide Palette" : "Show Palette"}
                >
                  {drawerOpen ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
                <IconButton size="small" onClick={() => {}} title="Settings">
                  <SettingsIcon />
                </IconButton>
                <IconButton size="small" onClick={handleSave} disabled={readOnly} title="Save">
                  <SaveIcon />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={handleRun}
                  title="Run"
                  sx={{ bgcolor: '#485869', color: 'white', '&:hover': { bgcolor: '#3A4754' } }}
                >
                  <RunIcon />
                </IconButton>
              </Paper>
            </Panel>

            {/* Workflow Info Panel */}
            <Panel position="top-left">
              <Paper sx={{ p: 2, minWidth: 220 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#485869', mb: 1 }}>
                  {workflowName}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Chip label={`${nodes.length} nodes`} size="small" variant="outlined" />
                  <Chip label={`${edges.length} connections`} size="small" variant="outlined" />
                </Box>
              </Paper>
            </Panel>
          </ReactFlow>
        </div>
      </Box>
    </Box>
  );
};

// Main Component Wrapper
const ChatWorkflowDesignerWrapper: React.FC<ChatWorkflowDesignerProps> = (props) => {
  return (
    <ReactFlowProvider>
      <ChatWorkflowDesigner {...props} />
    </ReactFlowProvider>
  );
};

export default ChatWorkflowDesignerWrapper;